// TODO: migrate from legacy CI tooling: resolve ts-* and/or eslint-* directives below (including inline comments)
/* eslint-disable @typescript-eslint/no-floating-promises, react-hooks/exhaustive-deps */

import React, { useEffect, useMemo, useState } from "react";

import PropTypes from "prop-types";

import { useDispatch, useSelector } from "react-redux";

import { useAppInsightsContext, useTrackEvent } from "@microsoft/applicationinsights-react-js";
import { Dimmer, Grid, Loader, Segment, Transition } from "semantic-ui-react";

import _ from "lodash";
import { useCookies } from "react-cookie";
import { useQuery } from "@tanstack/react-query";
import { useAuth0 } from "../../../auth/auth0";
import { useModuleNavigation } from "../../../hooks/useModuleNavigation";
import { useUserAuth } from "../../../hooks/useUserAuth";
import { useUserSettings } from "../../../hooks/useUserSettings";

import AP_Header from "./Header";
import LeftNav from "./LeftNav";
import LeftNavSecondary from "./LeftNavSecondary";

import * as entityApi from "../../../apis/entityApi";
import * as entityActions from "../../../redux/actions/entityActions";
import * as trialActions from "../../../redux/actions/trialActions";
import * as userActions from "../../../redux/actions/userActions";

import "./icons.css";

const unsetTopBottomPadding = {
	paddingTop: "unset",
	paddingBottom: "unset",
};

const Layout = ({ children }) => {
	//-- Instantiate hooks
	const dispatch = useDispatch();
	const { loading, isAuthenticated, getTokenSilently } = useAuth0();
	const userAuth = useUserAuth();
	const moduleNavigation = useModuleNavigation();
	const userSettings = useUserSettings();

	//-- Data Sources
	const user = useSelector((state) => (state.user ? state.user : null));
	const currentClientId = useSelector((state) => (state.clients ? state.clients.currentId : null));
	const [useSettingsNav, setUseSettingsNav] = useState(moduleNavigation.activeModule?.settings);
	const [cookies] = useCookies(["eulaAgreement"]);
	const [eulaAgreement, setEulaAgreement] = useState(false);

	const { flightId, fieldId, trialId, farmId, protocolId } = moduleNavigation;

	const { data: entityNames = null } = useQuery({
		queryKey: [entityApi.ENTITY_NAMES_QUERY_KEY, flightId, fieldId, trialId, farmId, protocolId],
		queryFn: async () => {
			const accessToken = await getTokenSilently();
			return entityApi.getEntityNamesAsync(flightId, fieldId, trialId, farmId, protocolId, accessToken);
		},
		enabled: userAuth.isReady && !!(flightId || fieldId || trialId || farmId || protocolId),
	});

	//-- Mirror into redux since several components still read entity names from there
	useEffect(() => {
		if (entityNames) {
			dispatch(entityActions.getEntityNamesSuccess(entityNames));
		}
	}, [entityNames]);

	const pageTitle = useMemo(() => {
		switch (moduleNavigation.activeModule?.name) {
			case "farms":
				return entityNames?.farmName
					? `${entityNames?.farmName}, ${entityNames?.cooperatorName} (${entityNames?.city}, ${entityNames?.region}, ${entityNames?.countryCode})`
					: "";
			case "trials":
				return entityNames?.trialName
					? `${entityNames?.trialName}, ${entityNames?.cooperatorName} (${entityNames?.city}, ${entityNames?.region}, ${entityNames?.countryCode})`
					: "";
			case "fields":
				return entityNames?.fieldName
					? `${entityNames?.fieldName}, ${entityNames?.cooperatorName} (${entityNames?.city}, ${entityNames?.region}, ${entityNames?.countryCode})`
					: "";
			case "flights":
				return entityNames?.flightName;
			case "protocols":
				return entityNames?.protocolName;
			default:
				return undefined;
		}
	}, [entityNames, moduleNavigation.activeModule?.name]);

	useEffect(() => {
		const submoduleTitle = moduleNavigation.activeSubmodule?.pageTitle;
		if (pageTitle && submoduleTitle) {
			document.title = `${submoduleTitle} - ${pageTitle} | aerialFARMS`;
		} else if (pageTitle) {
			document.title = `${pageTitle} | aerialFARMS`;
		} else if (submoduleTitle && submoduleTitle !== "Dashboard Overview") {
			document.title = `${submoduleTitle} | aerialFARMS`;
		} else {
			document.title = "aerialFARMS";
		}
	}, [pageTitle, moduleNavigation.activeSubmodule?.pageTitle]);

	//-- UI Control
	const [gettingUser, setGettingUser] = useState(false);

	//-- Application Insights
	const appInsights = useAppInsightsContext();
	const trackUser = useTrackEvent(appInsights, "User Login", user);

	useEffect(() => {
		let eulaAgreementFromCookie = _.some(cookies.eulaAgreement, (ua) => {
			if (user !== null && user.userInfo !== null) {
				return ua.userId === user.userInfo.basic.userId;
			}
		});

		setEulaAgreement(eulaAgreementFromCookie);

		if (!moduleNavigation.activeModule?.logout) {
			if (isAuthenticated && !user) {
				setGettingUser(true);
				getUserInfo();
			} else if (isAuthenticated && user && user.setupNeeded) {
				moduleNavigation.goToSetupNeeded();
				setGettingUser(false);
			} else if (userAuth.isReady && isAuthenticated && user) {
				if (!eulaAgreementFromCookie) {
					moduleNavigation.goToEula();
				} else if (!userAuth.isSubsriptionActive && !moduleNavigation.savedPathIsLink()) {
					moduleNavigation.goToSubscriptionExpired();
				}
			}
		}
	}, [isAuthenticated, user, cookies, userAuth.isReady]);

	useEffect(() => {
		setUseSettingsNav(moduleNavigation.activeModule?.settings);
	}, [moduleNavigation.activeModule?.settings]);

	useEffect(() => {
		if (!userAuth.isSubsriptionActive && userAuth.isReady === true) {
			moduleNavigation.goToSubscriptionExpired();
		}
	}, [userAuth.isSubsriptionActive]);

	async function getUserInfo() {
		const accessToken = await getTokenSilently();
		dispatch(userActions.logIn(accessToken)).then(() => {
			if (!userSettings.currentClientLogoUri) {
				userSettings.getClientLogoUri();
			}
		});
	}

	useEffect(() => {
		if (userAuth.isReady) {
			checkClient();
		}
	}, [userAuth.isReady, moduleNavigation.currentLocation]);

	useEffect(() => {
		if (user) {
			trackUser(user.userInfo?.basic);
		}
	}, [user]);

	async function checkClient() {
		const accessToken = await getTokenSilently();
		let switchingClients = false;
		dispatch(
			entityActions.checkClient(
				moduleNavigation.flightId,
				moduleNavigation.fieldId,
				moduleNavigation.trialId,
				moduleNavigation.protocolId,
				moduleNavigation.farmId,
				currentClientId,
				moduleNavigation.activeModule?.isStrict ?? moduleNavigation.activeSubmodule?.isStrict ?? false,
				accessToken,
			),
		).then((res) => {
			if (
				res &&
				currentClientId !== res &&
				user.userInfo.clientMemberships.filter((client) => client.clientId === res)
			) {
				userAuth.switchClient(currentClientId, res);
				switchingClients = true;
				if (isAuthenticated && user && !user?.setupNeeded) {
					setGettingUser(false);
				}
			} else if (
				moduleNavigation.flightId ||
				moduleNavigation.fieldId ||
				moduleNavigation.trialId ||
				moduleNavigation.farmId
			) {
				if (!res) {
					moduleNavigation.goToAccessDenied();
				} else if (!userAuth.isSubsriptionActive) {
					moduleNavigation.goToSubscriptionExpired();
				}
			}
			if (!switchingClients) {
				check();
			}
		});
	}

	function check() {
		if (
			userAuth.userCanAccessModule(moduleNavigation.activeModule) &&
			userAuth.userCanAccessSubmodule(moduleNavigation.activeSubmodule)
		) {
			//-- Set published trial as viewed (see api for implementation)
			if (moduleNavigation.qsPtfk) {
				setPublishedTrialFlightViewed(moduleNavigation.qsPtfk);
			}
			setGettingUser(false);
		} else {
			moduleNavigation.goToAccessDenied();
		}
	}

	async function setPublishedTrialFlightViewed(qsPtfk) {
		const accessToken = await getTokenSilently();
		dispatch(trialActions.setPublishedTrialFlightViewed(qsPtfk, accessToken));
	}

	return loading || gettingUser ? (
		<Loader active />
	) : (
		<>
			{isAuthenticated && user && !moduleNavigation.activeModule?.logout && !user?.setupNeeded && eulaAgreement
				? userAuth.isReady && !userAuth.isSubsriptionActive
					? renderSubscriptionExpired(children)
					: RenderAuthenticated(
							children,
							useSettingsNav,
							moduleNavigation.allModules,
							moduleNavigation.activeModule,
							moduleNavigation.currentSubmodules,
							moduleNavigation.activeSubmodule,
							userAuth,
							entityNames,
							pageTitle,
						)
				: (user || moduleNavigation.activeModule?.logout || !isAuthenticated) &&
					RenderUnauthenticated(moduleNavigation.activeModule?.secured, children)}
		</>
	);
};

const RenderUnauthenticated = (securedRoute, children) => {
	return (
		<Grid id="rootGrid" centered style={{ height: "100%", backgroundColor: "var(--nav-background)" }}>
			<Grid.Row id="rootGridContentRow" verticalAlign="middle" style={{ ...unsetTopBottomPadding }}>
				<Grid.Column width={7}>
					{/* Acounts for the case where the user navigates to a secured component w/o first logging */}
					{securedRoute ? (
						<Segment textAlign="center" style={{ minHeight: 116 }}>
							<Dimmer inverted active>
								<Loader inverted>Logging in</Loader>
								{children}
							</Dimmer>
						</Segment>
					) : (
						<>{children}</>
					)}
				</Grid.Column>
			</Grid.Row>
		</Grid>
	);
};

const RenderAuthenticated = (
	children,
	useSettingsNav,
	rootModules,
	activeModule,
	submodules,
	activeSubmodule,
	userAuth,
	entityNames,
	pageTitle,
) => {
	return (
		<Grid id="rootGrid" stretched>
			<Grid.Row id="rootGridHeaderRow" style={unsetTopBottomPadding}>
				<Grid.Column id="rootGridHeaderCol" style={{ padding: 0 }}>
					<AP_Header entityNames={entityNames} pageTitle={pageTitle} />
				</Grid.Column>
			</Grid.Row>
			{/* {userAuth.isSubsriptionActive ? (
				<Grid.Row id="rootGridSecondaryHeaderRow" style={unsetTopBottomPadding}>
					<Grid.Column id="rootGridSecondaryHeaderCol">
						<HeaderSecondary />
					</Grid.Column>
				</Grid.Row>
			) : null} */}

			{/* Height of the viewport minus the header and secondary header height */}
			<Grid.Row
				id="rootGridContentRow"
				style={{
					...unsetTopBottomPadding,
					flexGrow: "1",
					height: 0,
					border: "none",
				}}
			>
				{userAuth.isSubsriptionActive ? (
					<Grid.Column id="rootGridLeftNavCol" style={{ paddingLeft: "unset", paddingRight: "unset", width: 72 }}>
						<LeftNav
							navItems={
								useSettingsNav ? rootModules.filter((rm) => rm.settings) : rootModules.filter((rm) => !rm.settings)
							}
							activeItem={activeModule}
						/>
					</Grid.Column>
				) : null}
				{userAuth.isSubsriptionActive ? (
					<Grid.Column
						id="rootGridLeftNavSecondaryCol"
						style={{ paddingLeft: "unset", paddingRight: "unset", width: 59 }}
					>
						<Transition.Group animation="slide right" duration="100">
							{submodules.length > 0 && (
								<Grid.Column
									id="leftNavSecondaryTransitionContainer"
									style={activeModule.id === "dashboard" ? { background: "var(--nav-active)" } : {}}
								>
									<LeftNavSecondary navItems={submodules} activeItem={activeSubmodule} />
								</Grid.Column>
							)}
						</Transition.Group>
					</Grid.Column>
				) : null}
				<Grid.Column
					id="rootGridContentColumn"
					style={{
						paddingLeft: "unset",
						paddingRight: "unset",
						flexGrow: 1,
						height: "100%",
						overflow: "auto",
					}}
				>
					<Segment id="rootSegment" basic style={{ padding: "unset" }}>
						{children}
					</Segment>
				</Grid.Column>
			</Grid.Row>
		</Grid>
	);
};

const renderSubscriptionExpired = (children) => {
	return (
		<Grid id="rootGrid" stretched centered>
			<Grid.Row id="rootGridHeaderRow" style={unsetTopBottomPadding}>
				<Grid.Column id="rootGridHeaderCol">
					<AP_Header />
				</Grid.Column>
			</Grid.Row>

			{/* Height of the viewport minus the header and secondary header height */}
			<Grid.Row id="rootGridContentRow" style={{ ...unsetTopBottomPadding, height: "calc(100vh - 78px)" }}>
				<Grid.Column
					id="rootGridContentColumn"
					style={{ paddingLeft: "unset", paddingRight: "unset", width: "calc(100% - 110px)" }}
				>
					<Segment basic>{children}</Segment>
				</Grid.Column>
			</Grid.Row>
		</Grid>
	);
};
Layout.propTypes = {
	children: PropTypes.element.isRequired,
};

export default Layout;
